import {
  ACCESS_TOKEN,
  Application as AppEnum,
  authenticationService,
  AuthenticationUserType,
  axiosInstance,
  MBSAccessHelper,
  REFRESH_TOKEN,
  useAuthStore,
  useConfigStore,
} from '@fides/fides-react-components';
import { AxiosError, AxiosResponse } from 'axios';
import { dateFormatExample, endpoints, ENV } from '../consts';
import { getAppUrl, isLocalEnv } from '../helpers';
import { DefaultApps } from '../helpers/initApp';
import { AuthenticateResponse, Login } from '../interfaces';
import { Application, ExportSettings, PersonalSettings, User, UserSettings } from '../models';
import { ImpersonationClient } from '../models/ImpersonationClient';
import { ImpersonationUser } from '../models/ImpersonationUser';
import {
  AuthenticatePasswordChangeRequest,
  ChangePasswordRequest,
  DateType,
  JobsType,
  JsonObject,
  SecurityCodeResponse,
  ValidateResetPasswordTokenResponse,
} from '../types';

export const BE_PASSWORD_VALIDATION_ERROR = 'PASSWORD_VALIDATION_';

const setTokens = (accessToken: string, refreshToken: string): void => {
  const expirationDate = authenticationService.getTokenExpirationDate(refreshToken);
  authenticationService.saveTokenToCookies(ACCESS_TOKEN, accessToken, expirationDate);
  authenticationService.saveTokenToCookies(REFRESH_TOKEN, refreshToken, expirationDate);
};

const parseDataPersonalSettings = (data: PersonalSettings): JsonObject => {
  const dateTimeFormat: DateType = dateFormatExample.filter((item: DateType) =>
    item.value === data.dateFormat ? item.label : null
  )[0];
  return {
    calendarStartingDay: data.firstDayOfWeek,
    dateAndTimeFormat: dateTimeFormat.label,
    dateEnum: data.dateFormat,
    dateFormat: dateTimeFormat.label.split(' ')[0],
    language: data.language,
    numberFormat: data.numberFormat,
    payment: data.payment,
    keepDownloadCenter: data.keepDownloadCenter,
    paymentScreenLayout: data.paymentScreenLayout,
  };
};

const parseDataArsWebSettings = (data: UserSettings): JsonObject[] => [
  {
    consolidationCurrency: {
      code: data.personal.consolidatedCurrency,
    },
    downloadSettings: {
      MT942: {
        header: data.mt942.header,
        mt999Footer: data.mt942.footer,
        tag20: data.mt942.tag20,
        tag28: data.mt942.tag28,
        tag61: data.mt942.tag61,
        tag86: data.mt942.tag86,
        trBookingDate: data.mt942.bookingDate,
        withoutTransactions: data.mt942.withoutTransactions,
      },
      delimiter: data.export.delimiter,
      emptyPages: data.pdfReports.removeNoTransactionsPages,
      exportFormat: data.export.exportFormat,
      header: data.mt940.header,
      mt999Footer: data.mt940.footer,
      statementNo: data.mt940.statementNumber,
      tag20: data.mt940.tag20,
      tag28: data.mt940.tag28,
      tag61: data.mt940.tag61,
      tag64: data.mt940.tag64,
      tag65: data.mt940.tag65,
      tag86: data.mt940.tag86,
      trBookingDate: data.mt940.bookingDate,
      withoutTransactions: data.mt940.withoutTransactions,
    },
  },
];

const parseDataExportSettings = ({ exportFormat, delimiter }: ExportSettings): JsonObject => ({
  exportFormat,
  delimiter,
});

export const isUserLoggedIn = (clientId: string, userName: string, password: string): Promise<Login> =>
  axiosInstance
    .post(`${endpoints.PortalApiUrl}/${endpoints.authenticate}`, {
      browser: navigator.userAgent,
      clientId,
      password,
      username: userName,
    })
    .then((response: AxiosResponse<AuthenticateResponse | JsonObject>) => {
      if (response.status === 200) {
        setTokens(response.data.accessToken, response.data.refreshToken);
        return {
          secondFactorOptions: response.data.secondFactorOptions,
          user: new User(response.data.user),
        };
      }
      return Promise.reject(response);
    })
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const logout = (): Promise<void> =>
  axiosInstance
    .post(`${endpoints.PortalApiUrl}/${endpoints.logout}`)
    .then(() => {
      authenticationService.destroyTokens();
    })
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const sendSecurityCode = (code: string, setSecondFactorAuthenticationType: string): Promise<User> =>
  axiosInstance
    .post(`${endpoints.PortalApiUrl}/${endpoints.securityCode}`, {
      securityCode: code,
    })
    .then((response: SecurityCodeResponse) => {
      setTokens(response.data.accessToken, response.data.refreshToken);
      return new User({
        ...response.data.user,
        setSecondFactorAuthenticationType,
      });
    })
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const secondFactorSelect = (secondFactorValue: string): Promise<boolean> =>
  axiosInstance
    .post(`${endpoints.PortalApiUrl}/${endpoints.secondFactorSelect}`, `"${secondFactorValue}"`, {
      headers: { 'Content-Type': 'application/json;charset=UTF-8' },
    })
    .then(() => true)
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const exportDownloadSettings = (): Promise<JsonObject> =>
  axiosInstance
    .get(`${endpoints.ARSApiUrl}/${endpoints.profile}`)
    .then((response: AxiosResponse<JsonObject>) => ({
      ...response.data.downloadSettings,
      consolidationCurrency: response.data.consolidationCurrency,
    }))
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const applications = (): Promise<Application[]> =>
  axiosInstance
    .post(`${endpoints.PortalApiUrl}/${endpoints.applications}`)
    .then((response: AxiosResponse<JsonObject>) => response.data as Application[])
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const userBasicData = (appUrl: string): Promise<JsonObject> =>
  axiosInstance
    .post(`${appUrl}/${endpoints.user}`)
    .then((response: AxiosResponse<JsonObject>) => response.data)
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

const prepareApiUrls = (appList: Application[]): void => {
  DefaultApps.map((item) => {
    const [apiUrlKey, appName, transformApiUrl] = item;
    const appUrl = getAppUrl(appName as AppEnum, appList);
    return [apiUrlKey, appUrl ? transformApiUrl(appUrl) : appUrl];
  }).forEach(([apiUrlKey, apiUrl]) => {
    if (!isLocalEnv || apiUrl === undefined) Reflect.set(endpoints, apiUrlKey, apiUrl);
  });
};

export const userFullData = (): Promise<User> =>
  applications()
    .then(async (appList: Application[]) => {
      prepareApiUrls(appList);
      useConfigStore.setState({
        portalUrl: endpoints.PortalApiUrl.replace('/fidesportal', '/'),
        portalFEUrl: getAppUrl(AppEnum.PORTAL, appList) + ENV.PORTAL_DIRECTORY,
        eftUrl: getAppUrl(AppEnum.EFTWEB, appList),
      });
      const backendCalls = [userBasicData(endpoints.PortalApiUrl)];
      const ARSWeb = appList?.find((application) => application.name === AppEnum.ARSWEB);
      if (ARSWeb?.enabled) {
        backendCalls.push(exportDownloadSettings());
      }
      const data = await Promise.all(backendCalls as JobsType);
      const basicUser = data[0];
      const user = new User({
        ...basicUser,
        exportDownloadSettings: data[1] ? data[1] : undefined,
        applications: appList,
      });
      const mbsAccessHelper = new MBSAccessHelper(user);
      const isPartnerEndUser = mbsAccessHelper.isPartnerEndUser();
      user.isPartnerEndUser = isPartnerEndUser;
      basicUser.isPartnerEndUser = isPartnerEndUser;
      const isCorporateEndUser = mbsAccessHelper.isCorporateEndUser();
      user.isCorporateEndUser = isCorporateEndUser;
      basicUser.isCorporateEndUser = isCorporateEndUser;
      useAuthStore.setState({ user: basicUser as AuthenticationUserType });
      return user;
    })
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const resetPassword = (clientId: string, username = '', email = ''): Promise<boolean> =>
  axiosInstance
    .post(`${endpoints.PortalApiUrl}/${endpoints.resetPassword}`, {
      clientId,
      username,
      email,
    })
    .then(() => true)
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const validateResetPasswordToken = (resetPasswordToken: string): Promise<ValidateResetPasswordTokenResponse> =>
  axiosInstance
    .post(`${endpoints.PortalApiUrl}/${endpoints.authenticatePasswordReset}`, { resetPasswordToken })
    .then((response: AxiosResponse<ValidateResetPasswordTokenResponse>) => {
      setTokens(response.data.accessToken, response.data.refreshToken);
      return response.data;
    })
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const changePasswordStep = (data: AuthenticatePasswordChangeRequest): Promise<boolean> =>
  axiosInstance
    .post(`${endpoints.PortalApiUrl}/${endpoints.authenticatePasswordChange}`, data)
    .then(() => true)
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const secondFactorExpirationTime = (): Promise<number> =>
  axiosInstance
    .post(`${endpoints.PortalApiUrl}/${endpoints.requestSecondFactor}`)
    .then((response: AxiosResponse<number>) => response.data)
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const authenticateSecondFactor = (securityCode: string): Promise<number> =>
  axiosInstance
    .post(`${endpoints.PortalApiUrl}/${endpoints.authenticateSecondFactor}`, { securityCode })
    .then((response: AxiosResponse<number>) => response.data)
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const changePassword = (data: ChangePasswordRequest): Promise<void> =>
  axiosInstance
    .post(`${endpoints.PortalApiUrl}/${endpoints.changePassword}`, data)
    .then(() => Promise.resolve())
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const savePersonalSettings = (data: JsonObject): Promise<void> =>
  axiosInstance
    .put(`${endpoints.PortalApiUrl}/${endpoints.settings}`, data)
    .then(() => Promise.resolve())
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const saveArsWebSettings = async (data: JsonObject[]): Promise<void> => {
  const newData = [...data];
  if (newData[0]?.consolidationCurrency) {
    newData[1] = { consolidationCurrency: newData[0].consolidationCurrency };
    delete newData[0].consolidationCurrency;
  }
  try {
    await axiosInstance.put(`${endpoints.ARSApiUrl}/${endpoints.profile}`, newData);
    return await Promise.resolve();
  } catch (error) {
    throw new Error((error as Error).message);
  }
};

export const saveUserSettings = async (data: UserSettings, userId: string, hasARSWebAccess: boolean): Promise<void> => {
  const personalSettingsData: JsonObject = {
    ...parseDataPersonalSettings(data.personal),
    ...parseDataExportSettings(data.export),
    uniqueId: userId,
  };

  try {
    await savePersonalSettings(personalSettingsData);
    if (hasARSWebAccess) await saveArsWebSettings(parseDataArsWebSettings(data));
  } catch (error) {
    return Promise.reject(error);
  }

  return Promise.resolve();
};

export const clientList = (clientName = ''): Promise<ImpersonationClient[]> =>
  axiosInstance
    .get(`${endpoints.PortalApiUrl}/${endpoints.clients}`, {
      params: {
        page: 0,
        filter: { query: clientName },
        size: 100,
        sort: `clientId,asc`,
      },
    })
    .then((response: AxiosResponse<JsonObject>) =>
      response.data.content
        .filter((item: JsonObject) => item.active)
        .map((item: JsonObject) => new ImpersonationClient(item))
    )
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const userList = (clientId: string, userName: string): Promise<ImpersonationUser[]> =>
  axiosInstance
    .get(`${endpoints.EFTApiUrl}/${endpoints.impersonation}/${endpoints.impersonationUsers}/${clientId}`, {
      params: {
        clientId,
        page: 0,
        size: 100,
        sort: `username,asc`,
        username: userName,
      },
    })
    .then((response: AxiosResponse<JsonObject>) =>
      response.data.content.map((item: JsonObject) => new ImpersonationUser(item))
    )
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));

export const impersonate = (clientId: string, username: string): Promise<User> =>
  axiosInstance
    .post(`${endpoints.impersonation}/${endpoints.impersonate}`, null, {
      baseURL: endpoints.EFTApiUrl,
      params: { clientId, username },
    })
    .then((response: AxiosResponse<JsonObject>) => {
      setTokens(response.data.accessToken, response.data.refreshToken);
      return new User(response.data.user);
    })
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));
